import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from 'auth/auth.api';
import { useAuth } from 'auth/AuthProvider';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { Button } from 'shared/components/Button';
import { TextInput } from 'shared/components/Input';
import { OtpField } from 'shared/components/OtpField';
import { DangerIllustration } from 'shared/assets/illustrations';
import { Card } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';
import { useCountdown } from 'shared/hooks/useCountdown';
import { useConfirm } from 'shared/components/ConfirmDialog';

export function AccountDeletionPage() {
  const { t } = useI18n();
  const { clearSession } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [requested, setRequested] = useState(null);
  const [code, setCode] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const expires = useCountdown(requested?.expires_in_seconds, requested?.started_at);
  const resendIn = useCountdown(requested?.resend_after_seconds, requested?.started_at);

  async function requestCode() {
    if (requesting || (requested && resendIn > 0)) {
      return;
    }
    await confirm({
      title: t('deletion.title'),
      body: t('deletion.body'),
      action: async () => {
        setRequesting(true);
        setError('');
        try {
          const data = await authApi.requestDeletion();
          setRequested({ ...data, started_at: Date.now() });
        } catch (err) {
          setError(humanizeApiError(err, t));
        } finally {
          setRequesting(false);
        }
      },
    });
  }

  async function confirmDeletion(event) {
    event.preventDefault();
    if (confirming) {
      return;
    }
    setConfirming(true);
    setError('');
    try {
      await authApi.confirmDeletion({ code, confirmation });
      clearSession();
      navigate(paths.welcome, { replace: true, state: { deleted: true } });
    } catch (err) {
      setError(humanizeApiError(err, t));
    } finally {
      setConfirming(false);
    }
  }

  return (
    <Card className="form-page deletion-card">
      <PageMeta titleKey="meta.delete" />
      <div className="danger-tile"><DangerIllustration /></div>
      <h1>{t('deletion.title')}</h1>
      <p>{t('deletion.body')}</p>
      <p className="muted">{t('deletion.confirmExplain')}</p>
      <p className="muted">{t('deletion.destination')}</p>
      <Button onClick={requestCode} loading={requesting} disabled={Boolean(requested) && resendIn > 0}>
        {t('deletion.request')}
      </Button>
      {requested ? (
        <form onSubmit={confirmDeletion}>
          <p className="muted" aria-live="polite">{t('deletion.expires', { seconds: expires })}</p>
          <OtpField label={t('deletion.code')} value={code} onChange={setCode} />
          <TextInput
            id="confirmation"
            label={t('deletion.confirmation')}
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
          />
          {error ? <p className="field-error" role="alert">{error}</p> : null}
          <Button type="submit" tone="danger" loading={confirming} disabled={code.length !== 6 || confirmation !== 'DELETE'}>
            {t('actions.delete')}
          </Button>
        </form>
      ) : null}
      {error && !requested ? <p className="field-error" role="alert">{error}</p> : null}
    </Card>
  );
}
